import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Play, Pause, SkipForward, SkipBack, Plus, Minus, Tv, Pencil, RotateCcw, Copy, RefreshCw, Megaphone, Music, X, Upload } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { NumberInput } from '../components/NumberInput';
import { useApp } from '../context/AppContext';
import { useToast } from '../hooks/use-toast';
import { computeRemaining, StaticDigits } from './ClockView';

function fmt(sec) {
  const s = Math.max(0, Math.floor(sec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
}

export default function TournamentControl() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, loading, getById, dispatchAction, updateBanner, subscribeCode, getServerNow, refreshTv, fetchFullTournament } = useApp();
  const { toast } = useToast();
  const t = getById(id);

  React.useEffect(() => { if (!loading && !user) nav('/admin/login'); }, [user, loading, nav]);

  // При монтировании подгружаем ПОЛНОЕ состояние турнира (в т.ч.
  // bannerSound.dataUrl), т.к. список /api/tournaments отдаёт lite-версию
  // без тяжёлых base64. Без этого нельзя было бы прослушать звук банера в
  // админке — только загрузить новый.
  React.useEffect(() => {
    if (id && user) fetchFullTournament(id);
  }, [id, user, fetchFullTournament]);

  // Подписываемся на WS по коду — обновления от сервера прилетят и в /admin,
  // и в /clock. Клиенты рендерят один и тот же серверный state.
  React.useEffect(() => {
    if (!t?.code) return;
    const unsub = subscribeCode(t.code);
    return () => unsub && unsub();
  }, [t?.code, subscribeCode]);

  // Тик отрисовки для плавного отсчёта таймера.
  const [, setTick] = React.useState(0);
  React.useEffect(() => {
    const i = setInterval(() => setTick((n) => (n + 1) % 1e6), 250);
    return () => clearInterval(i);
  }, []);

  // ---- Баннер по центру ТВ (хуки должны быть ДО early return) ----
  const bannerServer = t?.banner || { enabled: false, text: '' };
  const [bannerDraft, setBannerDraft] = React.useState(bannerServer.text || '');
  const [bannerFocused, setBannerFocused] = React.useState(false);
  const bannerFileRef = React.useRef(null);
  const skipBannerBlurCommitRef = React.useRef(false);
  const [bannerSoundError, setBannerSoundError] = React.useState('');
  React.useEffect(() => {
    // Пока пользователь не редактирует, синхронизируем input с server state
    // (например, если банер отредактировали в другой вкладке).
    if (!bannerFocused) setBannerDraft(bannerServer.text || '');
  }, [bannerServer.text, bannerFocused]);

  if (!user || !t) return null;

  const remaining = computeRemaining(t, getServerNow ? getServerNow() : undefined);
  const level = t.blinds[t.currentLevelIndex] || {};
  const next = t.blinds[Math.min(t.currentLevelIndex + 1, t.blinds.length - 1)] || {};
  const players = t.players || { total: 0, remaining: 0 };

  // --- Все обработчики теперь просто отправляют action. Никаких refs, никаких
  // локальных дельт — сервер сам считает и возвращает финальный state. ---
  const startPause = () => {
    if (!t.isRunning || t.isPaused) dispatchAction(id, 'start');
    else dispatchAction(id, 'pause');
  };
  const nextLevel = () => dispatchAction(id, 'step_level', { delta: +1 });
  const prevLevel = () => dispatchAction(id, 'step_level', { delta: -1 });
  const resetLevel = () => dispatchAction(id, 'reset_level');
  const gotoLevel = (idx) => dispatchAction(id, 'goto_level', { index: idx });
  const adjustTime = (delta) => dispatchAction(id, 'adjust_time', { delta });
  const incTotal = (delta) => dispatchAction(id, 'adjust_players_total', { delta });
  const incRemaining = (delta) => dispatchAction(id, 'adjust_players_remaining', { delta });
  const setTotal = (value) => dispatchAction(id, 'set_players_total', { value });
  const setRemaining = (value) => dispatchAction(id, 'set_players_remaining', { value });

  const commitBannerText = () => {
    const clean = (bannerDraft || '').trim();
    if (clean !== (bannerServer.text || '').trim()) {
      updateBanner(id, { text: clean });
    }
  };
  const toggleBanner = (enabled) => {
    skipBannerBlurCommitRef.current = true;
    const clean = (bannerDraft || '').trim();
    const patch = { enabled };
    if (clean !== (bannerServer.text || '').trim()) patch.text = clean;
    updateBanner(id, patch);
  };
  const armSkipBannerBlurCommit = () => {
    // pointerdown/focus на Switch срабатывает ДО blur textarea.
    skipBannerBlurCommitRef.current = true;
  };
  const onBannerTextBlur = () => {
    setBannerFocused(false);
    if (skipBannerBlurCommitRef.current) {
      skipBannerBlurCommitRef.current = false;
      return;
    }
    commitBannerText();
  };

  // ---- Звук банера ----
  const bannerSoundServer = t?.bannerSound || null;
  const onPickBannerSound = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBannerSoundError('');
    if (!file.type.startsWith('audio/')) {
      setBannerSoundError('Выберите аудиофайл (MP3, WAV, OGG)');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setBannerSoundError('Файл больше 2 МБ. Сожмите или укоротите.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      updateBanner(id, { soundName: file.name, soundDataUrl: reader.result });
      toast({ title: 'Звук банера загружен', description: file.name });
    };
    reader.readAsDataURL(file);
    // сбрасываем input, чтобы можно было выбрать тот же файл повторно
    if (bannerFileRef.current) bannerFileRef.current.value = '';
  };
  const clearBannerSound = () => {
    updateBanner(id, { clearSound: true });
  };
  const previewBannerSound = () => {
    if (!bannerSoundServer?.dataUrl) return;
    try {
      const a = new Audio(bannerSoundServer.dataUrl);
      a.play().catch(() => {});
    } catch (e) { /* noop */ }
  };

  const copyCode = () => { navigator.clipboard.writeText(t.code); toast({ title: 'Код скопирован', description: t.code }); };
  const statusText = t.isRunning ? (t.isPaused ? 'Пауза' : 'В эфире') : 'Ожидание';

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="border-b border-neutral-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-2 px-3 py-3 sm:flex-nowrap sm:px-6 sm:py-4">
          <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
            <Button
              variant="ghost"
              onClick={() => nav('/admin')}
              className="shrink-0 px-2 text-neutral-600 hover:text-red-600 sm:px-3"
             
              aria-label="К турнирам"
            >
              <ArrowLeft className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">К турнирам</span>
            </Button>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 sm:text-xs">Управление</div>
              <div className="truncate text-base font-bold sm:text-lg">{t.name}</div>
            </div>
          </div>
          <div className="flex w-full shrink-0 items-center justify-end gap-1.5 sm:w-auto sm:gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={async () => { const ok = await refreshTv(id); toast({ title: ok ? 'ТВ обновляется' : 'Ошибка обновления', description: ok ? 'Все подключённые экраны перезагружаются' : 'Попробуйте ещё раз' }); }}
              className="px-2 sm:px-3"
             
              aria-label="Обновить ТВ"
            >
              <RefreshCw className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Обновить ТВ</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => nav(`/admin/tournament/${t.id}/edit`)}
              className="px-2 sm:px-3"
             
              aria-label="Редактировать"
            >
              <Pencil className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Редактировать</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(`/clock/${t.code}`, '_blank')}
              className="px-2 sm:px-3"
             
              aria-label="Открыть ТВ"
            >
              <Tv className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Открыть ТВ</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-6xl gap-6 px-4 py-8 sm:px-6 lg:grid-cols-3">
        <Card className="col-span-full overflow-hidden rounded-2xl border-neutral-800 bg-neutral-950 text-white">
          <CardContent className="p-8">
            <div className="flex items-center justify-between text-sm text-neutral-400">
              <div className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${t.isRunning && !t.isPaused ? 'bg-emerald-500' : 'bg-neutral-500'}`} />
                {statusText}
              </div>
              <button onClick={copyCode} className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 hover:bg-white/10">
                Код <span className="font-clock font-bold text-white">{t.code}</span> <Copy className="h-3 w-3" />
              </button>
            </div>
            <div className="mt-4 grid gap-4 md:grid-cols-[1fr_2fr_1fr] md:items-center">
              <div className="text-center md:text-right">
                <div className="text-xs uppercase tracking-widest text-neutral-400">{level.isBreak ? 'Перерыв' : `Уровень ${level.level}`}</div>
                {level.isBreak ? (
                  <div className="font-clock text-4xl font-bold text-amber-300">{level.breakLabel}</div>
                ) : (
                  <>
                    <div className="font-clock text-5xl font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {(level.sb || 0).toLocaleString('ru-RU')} / {(level.bb || 0).toLocaleString('ru-RU')}
                    </div>
                    {level.ante > 0 && <div className="mt-1 text-amber-400">Анте {level.ante.toLocaleString('ru-RU')}</div>}
                  </>
                )}
              </div>
              <div className="text-center">
                <div className="font-clock text-8xl font-bold sm:text-9xl" style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 900 }}>
                  <StaticDigits text={fmt(remaining)} />
                </div>
              </div>
              <div className="text-center md:text-left">
                <div className="text-xs uppercase tracking-widest text-neutral-400">Далее</div>
                {next?.isBreak ? (
                  <div className="font-clock text-3xl font-bold text-amber-300/80">{next.breakLabel}</div>
                ) : (
                  <div className="font-clock text-4xl font-bold text-neutral-400" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {(next?.sb || 0).toLocaleString('ru-RU')} / {(next?.bb || 0).toLocaleString('ru-RU')}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl lg:col-span-2">
          <CardContent className="p-6">
            <div className="text-xs font-semibold uppercase tracking-widest text-red-600">Управление часами</div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button onClick={startPause} className="h-12 bg-red-600 px-6 text-base text-white hover:bg-red-700">
                {t.isRunning && !t.isPaused ? (<><Pause className="mr-2 h-4 w-4" /> Пауза</>) : (<><Play className="mr-2 h-4 w-4" /> {t.isRunning ? 'Продолжить' : 'Старт'}</>)}
              </Button>
              <Button variant="outline" className="h-12" onClick={prevLevel}><SkipBack className="mr-2 h-4 w-4" /> Пред. уровень</Button>
              <Button variant="outline" className="h-12" onClick={nextLevel}><SkipForward className="mr-2 h-4 w-4" /> След. уровень</Button>
              <Button variant="outline" className="h-12" onClick={resetLevel}><RotateCcw className="mr-2 h-4 w-4" /> Сбросить</Button>
            </div>
            <div className="mt-6 text-xs font-semibold uppercase tracking-widest text-neutral-500">Коррекция времени</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {[-600, -60, -30, -10, +10, +30, +60, +600].map((s) => (
                <Button key={s} size="sm" variant="outline" onClick={() => adjustTime(s)}>
                  {s === -600 ? '−10 мин' : s === 600 ? '+10 мин' : (s > 0 ? `+${s}с` : `${s}с`)}
                </Button>
              ))}
            </div>

            <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
              {/* Всего */}
              <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
                <div className="text-xs uppercase text-neutral-500">Всего игроков</div>
                <div className="mt-2 flex items-center gap-2">
                  <button
                    onClick={() => incTotal(-1)}
                    className="flex h-10 w-10 items-center justify-center rounded-md border border-neutral-200 bg-white hover:bg-neutral-100 active:bg-neutral-200"
                    aria-label="Уменьшить"
                   
                  ><Minus className="h-4 w-4" /></button>
                  <NumberInput
                    value={players.total}
                    onChange={setTotal}
                    className="h-10 flex-1 text-center font-clock text-xl font-bold"
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                   
                  />
                  <button
                    onClick={() => incTotal(1)}
                    className="flex h-10 w-10 items-center justify-center rounded-md border border-neutral-200 bg-white hover:bg-neutral-100 active:bg-neutral-200"
                    aria-label="Увеличить"
                   
                  ><Plus className="h-4 w-4" /></button>
                </div>
              </div>

              {/* Осталось */}
              <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
                <div className="text-xs uppercase text-neutral-500">Осталось игроков</div>
                <div className="mt-2 flex items-center gap-2">
                  <button
                    onClick={() => incRemaining(-1)}
                    className="flex h-10 w-10 items-center justify-center rounded-md border border-neutral-200 bg-white hover:bg-neutral-100 active:bg-neutral-200"
                    aria-label="Уменьшить"
                   
                  ><Minus className="h-4 w-4" /></button>
                  <NumberInput
                    value={players.remaining}
                    onChange={setRemaining}
                    className="h-10 flex-1 text-center font-clock text-xl font-bold"
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                   
                  />
                  <button
                    onClick={() => incRemaining(1)}
                    className="flex h-10 w-10 items-center justify-center rounded-md border border-neutral-200 bg-white hover:bg-neutral-100 active:bg-neutral-200"
                    aria-label="Увеличить"
                   
                  ><Plus className="h-4 w-4" /></button>
                </div>
                <div className="mt-1 text-[11px] text-neutral-500">максимум — «Всего игроков» ({players.total})</div>
              </div>
            </div>

            {/* Баннер по центру ТВ — переопределяет отображение часов
                (показывает крупный жирный текст). Идеально для сообщений
                типа «ФИНАЛЬНЫЙ СТОЛ», «5 МИНУТ ДО СТАРТА» и т.п. */}
            <div className="mt-8 rounded-lg border border-amber-200 bg-amber-50/50 p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Megaphone className="h-4 w-4 text-amber-700" />
                  <div className="text-xs font-semibold uppercase tracking-widest text-amber-800">Баннер на ТВ</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-neutral-600">
                    {bannerServer.enabled ? 'Показан' : 'Скрыт'}
                  </span>
                  <Switch
                    checked={!!bannerServer.enabled}
                    onPointerDown={armSkipBannerBlurCommit}
                    onFocus={armSkipBannerBlurCommit}
                    onCheckedChange={toggleBanner}
                    aria-label="Показать баннер на ТВ"
                  />
                </div>
              </div>
              <div className="mt-3">
                <Textarea
                  value={bannerDraft}
                  onChange={(e) => setBannerDraft(e.target.value)}
                  onFocus={() => setBannerFocused(true)}
                  onBlur={onBannerTextBlur}
                  placeholder="Например: ФИНАЛЬНЫЙ СТОЛ&#10;осталось 9 игроков"
                  className="min-h-[88px] text-base font-semibold leading-snug"
                 
                  maxLength={200}
                  rows={3}
                />
                <div className="mt-1 text-[11px] text-neutral-500">
                  Enter — новая строка (абзац). Сохраняется автоматически при клике вне поля. Текст показывается на ТВ крупно и жирно поверх часов.
                </div>
              </div>

              {/* Звук банера — играется на ТВ один раз при активации банера */}
              <div className="mt-3 rounded-md border border-amber-200 bg-white p-3">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-amber-800">
                  <Music className="h-3.5 w-3.5" /> Звук банера
                </div>
                <input
                  ref={bannerFileRef}
                  type="file"
                  accept="audio/*"
                  onChange={onPickBannerSound}
                  className="hidden"
                 
                />
                {bannerSoundServer?.name ? (
                  <div className="mt-2 flex items-center gap-2 rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2">
                    <Music className="h-4 w-4 text-amber-600" />
                    <div className="min-w-0 flex-1 truncate text-sm font-medium">
                      {bannerSoundServer.name}
                    </div>
                    {bannerSoundServer.dataUrl && (
                      <button type="button" onClick={previewBannerSound} className="rounded-md border border-neutral-200 bg-white p-1.5 hover:bg-neutral-100" title="Прослушать">
                        <Play className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button type="button" onClick={() => bannerFileRef.current?.click()} className="rounded-md border border-neutral-200 bg-white p-1.5 hover:bg-neutral-100" title="Заменить">
                      <Upload className="h-3.5 w-3.5" />
                    </button>
                    <button type="button" onClick={clearBannerSound} className="rounded-md border border-neutral-200 bg-white p-1.5 text-neutral-400 hover:text-red-600" title="Удалить">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => bannerFileRef.current?.click()}
                    className="mt-2 flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-neutral-300 bg-neutral-50 py-2 text-xs text-neutral-700 hover:bg-neutral-100"
                   
                  >
                    <Upload className="h-3.5 w-3.5" /> Загрузить аудио (MP3/WAV, до 2 МБ)
                  </button>
                )}
                {bannerSoundError && (
                  <div className="mt-2 text-xs font-medium text-red-600">{bannerSoundError}</div>
                )}
                <div className="mt-2 text-[11px] text-neutral-500">
                  Играется на ТВ один раз при появлении банера.
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="text-xs font-semibold uppercase tracking-widest text-red-600">Структура</div>
              <div className="text-xs text-neutral-500">{t.blinds.length} уровней</div>
            </div>
            <div className="mt-4 max-h-96 space-y-1 overflow-y-auto pr-2">
              {t.blinds.map((b, i) => (
                <button
                  key={i}
                  onClick={() => gotoLevel(i)}
                  className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm ${i === t.currentLevelIndex ? 'bg-red-50 text-red-700' : b.isBreak ? 'bg-amber-50 text-amber-800' : 'hover:bg-neutral-100'}`}
                >
                  <div className="flex-1">
                    <div className="font-semibold">{b.isBreak ? (b.breakLabel || 'Перерыв') : `Уровень ${b.level}`}</div>
                    <div className="text-xs opacity-70">{b.isBreak ? `${b.duration} мин` : `${b.sb}/${b.bb}${b.ante ? ' · анте ' + b.ante : ''} · ${b.duration} мин`}</div>
                  </div>
                  {i === t.currentLevelIndex && <div className="text-xs font-bold uppercase">Сейчас</div>}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
